import { Popover } from '@mui/material'
import { Switch } from '@repo/design-system/index'
import {
  QIcon_chat,
  QIcon_download,
  QIcon_file,
  QIcon_headphone,
  QIcon_text,
  QIcon_text_with_small_text,
} from '@repo/icons/index'
import classNames from 'classnames'
import { addYears } from 'date-fns'
import { useRouter } from 'next/router'
import { useContext, useEffect, useRef, useState } from 'react'
import ReactToPrint from 'react-to-print'
import Cookies from 'universal-cookie'
import { COOKIE_DOMAIN } from '../../../modules/envVars'
import useIsAdminLogin from '../../../modules/hooks/common/useIsAdminLogin'
import { n_material } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { track } from '../../../modules/track'
import { track_click } from '../../../modules/track_click'
import { COLORS, LOCATION, LOG_CATEGORY, LOG_TYPE } from '../../../modules/vars'
import { TEXT_SIZE, TextContext } from '../../../pages/[lang]/portal/materials/detail/[materialId]'
import PdfDocument from './MaterialPdfDocument'

const MaterialInsightTitleSection = ({ insightData, materialData, isLesson, isModal = false }) => {
  const { t, isLocaleKO } = useTranslation()
  const router = useRouter()
  const isAdminLogin = useIsAdminLogin()
  const textRef = useRef(null)
  const downloadRef = useRef(null)
  const pdfRef = useRef(null)
  const { showKorean, setShowKorean, textSize, setTextSize } = useContext(TextContext)

  const [showTextTooltip, setShowTextTooltip] = useState<boolean>(false)
  const [showDownloadTooltip, setShowDownloadTooltip] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const isTextSettingChanged = showKorean || textSize !== TEXT_SIZE.DEFAULT
  const cookies = new Cookies()
  const BUTTON_NOTICE_COOKIE = 'show_button_notice_tooltip'
  const showButtonNoitceTooltip = cookies.get(BUTTON_NOTICE_COOKIE) === 'close'

  const handleMp3ButtonClick = () => {
    if (isAdminLogin) return
    const url = router.asPath
    // duration 없이 트래킹
    track({
      location: LOCATION.COURSE,
      log_type: LOG_TYPE.COURSE.MP3_LISTEN,
      log_category: LOG_CATEGORY.LISTEN,
      course_id: materialData?.course?.id,
    })

    track_click({
      container: `${url}`,
      target: `mp3/course/${materialData?.course?.id}`,
      platform: 'web',
    })
  }

  const handlePdfClick = () => {
    if (isAdminLogin) return
    const url = router.asPath
    track_click({
      container: `${url}`,
      target: `pdf/course/${materialData?.course?.id}`,
      platform: 'web',
    })
  }

  useEffect(() => {
    function handleClickOutside(event) {
      // MUI Popover 사용 시에는 이 로직을 사용하지 않음
      // Popover 자체 onClose로 처리

      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setShowDownloadTooltip(false)
      }
    }

    // Use capture phase to intercept events earlier
    document.addEventListener('mousedown', handleClickOutside, true)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true)
    }
  }, [downloadRef]) // textRef 제거

  const getLevel = (level = 0) => {
    switch (level) {
      case 0:
        return 'Basic'
      case 1:
        return 'Intermediate'
      case 2:
        return 'Advanced'
      case 3:
        return 'All'
    }
  }
  return (
    <div className="relative">
      {materialData.course && (
        <>
          <div className="flex h-full w-full flex-col">
            <div className="flex flex-col">
              <div className="mb-[12px] flex flex-row gap-[4px]">
                <div className="Caption-12Bd rounded-[4px] bg-gray-100 px-[6px] py-[2px] text-gray-700">
                  {materialData?.course?.category}
                </div>
              </div>
              <div className="Hero3-36Bd">
                {!showKorean
                  ? materialData?.course?.title_en
                    ? materialData?.course?.title_en
                    : materialData?.course?.title
                  : materialData?.course?.title_kr
                  ? materialData?.course?.title_kr
                  : materialData?.course?.title}
              </div>
              <div className="Title1-20Md">
                {!showKorean ? materialData?.course?.subtitle_en : materialData?.course?.subtitle_kr}
              </div>
              <div
                className={classNames(
                  isModal ? 'flex-col items-start justify-start gap-[12px]' : 'flex-row items-center justify-between',
                  'Body1-16Md mt-[20px] mb-[39px] flex text-gray-500'
                )}
              >
                <div className="flex flex-row">
                  <div className="Body1-16Md border-r-1 mr-[12px] pr-[12px] text-gray-500">
                    Updated: {materialData.course.published_at || materialData.course.updated_at}
                  </div>
                  <div className="">
                    {materialData.course.mp3_running_time_in_minute} min read · {getLevel(materialData.course.level)}
                  </div>
                </div>
                <div className={classNames(isModal ? 'hidden sm:flex' : 'hidden', 'flex-row gap-[10px] sm:flex')}>
                  <div className="relative" ref={textRef}>
                    {!isModal && !showButtonNoitceTooltip ? (
                      <>
                        <button
                          className={classNames(
                            isTextSettingChanged && 'border-purple-500',
                            'rounded-[7px] border-[1.5px] py-[11px] px-[10px]'
                          )}
                          onClick={(event) => {
                            cookies.set(BUTTON_NOTICE_COOKIE, 'close', {
                              domain: COOKIE_DOMAIN,
                              path: '/',
                              expires: addYears(new Date(), 1),
                            })
                            setAnchorEl(event.currentTarget)
                            setShowTextTooltip((prev) => !prev)
                          }}
                        >
                          <QIcon_text
                            width={24}
                            height={24}
                            color={isTextSettingChanged ? COLORS.PURPLE500 : COLORS.BLACK}
                          />
                        </button>
                      </>
                    ) : (
                      <button
                        className={classNames(
                          isTextSettingChanged && 'border-purple-500',
                          'rounded-[7px] border-[1.5px] py-[11px] px-[10px]'
                        )}
                        onClick={(event) => {
                          cookies.remove(BUTTON_NOTICE_COOKIE)
                          setAnchorEl(event.currentTarget)
                          setShowTextTooltip((prev) => !prev)
                        }}
                      >
                        <QIcon_text
                          width={24}
                          height={24}
                          color={isTextSettingChanged ? COLORS.PURPLE500 : COLORS.BLACK}
                        />
                      </button>
                    )}

                    <Popover
                      open={showTextTooltip}
                      anchorEl={anchorEl}
                      onClose={(event, reason) => {
                        // ESC 키와 백드롭 클릭으로 닫기 허용
                        if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
                          setShowTextTooltip(false)
                          setAnchorEl(null)
                        }
                      }}
                      disableScrollLock
                      disableAutoFocus
                      disableEnforceFocus
                      disableRestoreFocus
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                      }}
                      style={{
                        marginTop: '8px',
                        borderRadius: '5px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      }}
                    >
                      <div className="bg-white rounded-[5px]">
                        <div className="border-1 rounded-[7px]">
                          {isLocaleKO && (
                            <>
                              <div
                                className="flex flex-col items-center justify-center p-[16px]"
                                style={{ pointerEvents: 'auto', position: 'relative', zIndex: 10001 }}
                              >
                                <div
                                  className="flex w-full flex-row justify-between"
                                  style={{ pointerEvents: 'auto', position: 'relative', zIndex: 10002 }}
                                >
                                  <div className="flex flex-row items-center gap-[4px]">
                                    <QIcon_chat width={20} height={20} />
                                    <div className="Body2-14Md text-gray-700">한글 같이 보기 </div>
                                  </div>
                                  <div
                                    className="flex items-center switch-component"
                                    data-switch-container="true"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      console.log('Switch container clicked!')
                                      setShowKorean(!showKorean)
                                    }}
                                    style={{
                                      position: 'relative',
                                      zIndex: 10003,
                                      pointerEvents: 'auto',
                                      cursor: 'pointer',
                                      minWidth: '50px',
                                      minHeight: '30px',
                                    }}
                                  >
                                    <div data-switch="true" style={{ pointerEvents: 'auto' }}>
                                      <Switch checked={showKorean} setChecked={setShowKorean} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <hr className="mx-[16px] bg-gray-200" />
                            </>
                          )}
                          <div className="p-[16px]">
                            <div className="mb-[12px] flex flex-row gap-[4px]">
                              <QIcon_text_with_small_text color={COLORS.GRAY500} width={20} height={20} />
                              <div className="Body2-14Md text-gray-700">{t(n_material.font_size)}</div>
                            </div>
                            <div className="Body2-14Md flex flex-row gap-[8px]">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setTextSize(TEXT_SIZE.SMALLER)
                                }}
                                className={classNames(
                                  textSize === TEXT_SIZE.SMALLER && 'border-purple-500 bg-purple-50 text-purple-500',
                                  'rounded-[8px] border-[1.5px] py-[8px] px-[12px]'
                                )}
                              >
                                {t(n_material.font_size_small)}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setTextSize(TEXT_SIZE.DEFAULT)
                                }}
                                className={classNames(
                                  textSize === TEXT_SIZE.DEFAULT && 'border-purple-500 bg-purple-50 text-purple-500',
                                  'rounded-[8px] border-[1.5px] py-[8px] px-[12px]'
                                )}
                              >
                                {t(n_material.font_size_normal)}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setTextSize(TEXT_SIZE.BIGGER)
                                }}
                                className={classNames(
                                  textSize === TEXT_SIZE.BIGGER && 'border-purple-500 bg-purple-50 text-purple-500',
                                  'rounded-[8px] border-[1.5px] py-[8px] px-[12px]'
                                )}
                              >
                                {t(n_material.font_size_big)}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Popover>
                  </div>
                  {!isModal && (
                    <div className="relative" ref={downloadRef}>
                      <button
                        className="rounded-[7px] border-[1.5px] py-[11px] px-[10px]"
                        onClick={() => setShowDownloadTooltip((prev) => !prev)}
                      >
                        <QIcon_download width={24} height={24} color={COLORS.BLACK} />
                      </button>
                      {showDownloadTooltip && (
                        <div className="speech-bubble-download absolute right-[120%] top-[60px] z-[120] w-[156px] translate-x-2/4 rounded-[5px] bg-white shadow-md">
                          <div className="border-1 rounded-[5px] px-[20px] py-[9px]">
                            <div className="flex flex-row items-center justify-center gap-[20px]">
                              {materialData.course.mp3_link && (
                                <div className="flex flex-col items-center justify-center">
                                  <a
                                    onClick={() => handleMp3ButtonClick()}
                                    rel="noreferrer"
                                    href={materialData.course.mp3_link}
                                    className="Caption-12Md flex flex-col items-center justify-center"
                                  >
                                    <div className=" flex items-center justify-center rounded-full bg-gray-100 p-[12px] text-gray-700">
                                      <QIcon_headphone />
                                    </div>
                                    MP3
                                  </a>
                                </div>
                              )}
                              <div className="flex flex-col items-center justify-center">
                                <ReactToPrint
                                  trigger={() => (
                                    <button
                                      rel="noreferrer"
                                      className="Caption-12Md flex flex-col items-center justify-center"
                                      onClick={() => handlePdfClick()}
                                    >
                                      <div className=" flex items-center justify-center rounded-full bg-gray-100 p-[12px] text-gray-700">
                                        <QIcon_file />
                                      </div>
                                      PDF
                                    </button>
                                  )}
                                  content={() => pdfRef.current}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {materialData?.course?.image_wide_url && (
        <div className="">
          <img src={materialData.course.image_wide_url} alt="material_image" className="w-full rounded-[8px]" />
        </div>
      )}
      <PdfDocument insightData={insightData} materialData={materialData} ref={pdfRef} />
    </div>
  )
}

export default MaterialInsightTitleSection
